import React, { Suspense } from 'react';
import get from 'lodash-es/get';
import classNames from 'clsx';

import retryImport from 'app/shared/helpers/retryImport';
import lazyWithReload from 'app/shared/helpers/lazyWithReload';
import { DictionaryEntry } from 'app/services/dict.service';
import { SelectWithFilterInput } from 'app/components/SelectWithFilterInput';
import { SelectWithFilterPaper } from 'app/components/SelectWithFilterPaper';
import { InputPlaceholder } from 'app/components/InputPlaceholder';
import { useSelectWithFilter } from './useSelectWithFilter';
import { SelectWithFilterProps } from './SelectWithFilter.interfaces';
import { useStyles } from './SelectWithFilter.styles';
import { ControlLabelEnum, FieldsEnum, LabelEnum } from 'app/shared/enums';

const Downshift = lazyWithReload(() => retryImport(() => import('downshift')));

const SelectWithFilter = (props: SelectWithFilterProps) => {
  const {
    value,
    dictionary,
    readOnly,
    disabled,
    cols,
    onClick,
    answerGroups,
    error,
    focused,
    label = '',
    width,
    name,
    margin,
    inputProps,
    withoutStartAdornment,
    className,
    isTravelResultsFilters,
  } = props;

  const classes = useStyles(props);

  const {
    isMobile,
    getSelectedItem,
    handleOnChange,
    onInputChange,
    getCurrentInputProps,
    getInputClasses,
    setupEventProps,
    getHasSublabels,
  } = useSelectWithFilter(props);

  const currentSelectedItem = getSelectedItem(value) || false;

  const fieldsWithLabelNeeded = {
    [FieldsEnum.Sort]: LabelEnum.Sort,
    [FieldsEnum.CarProductionYear]: LabelEnum.ProductionYear,
    [FieldsEnum.CarMake]: LabelEnum.Make,
    [FieldsEnum.CarModel]: ControlLabelEnum.Model,
  };

  return (
    <Suspense fallback={<InputPlaceholder width={width} />}>
      {name && name in fieldsWithLabelNeeded && (
        <label htmlFor={name} id={`${name}-label`} style={{ display: 'none' }}>
          {fieldsWithLabelNeeded[name as keyof typeof fieldsWithLabelNeeded]}
        </label>
      )}
      <div
        className={classNames(classes.root, className, {
          [classes.withSubLabels]:
            currentSelectedItem && currentSelectedItem.subLabels,
        })}
      >
        <Downshift
          id={name}
          onChange={handleOnChange}
          itemToString={(item: DictionaryEntry) =>
            item
              ? isMobile
                ? item.valueShort || item.value || ''
                : item.value || ''
              : ''
          }
          selectedItem={currentSelectedItem}
          initialInputValue={
            isMobile
              ? get(
                  currentSelectedItem,
                  'valueShort',
                  get(currentSelectedItem, 'value', '')
                )
              : get(currentSelectedItem, 'value', '')
          }
          onInputValueChange={onInputChange}
        >
          {({
            getInputProps,
            getItemProps,
            isOpen,
            openMenu,
            closeMenu,
            inputValue,
            selectedItem,
            clearSelection,
            highlightedIndex,
            selectItem,
          }) => (
            <div className={classes.container}>
              <SelectWithFilterInput
                withoutStartAdornment={withoutStartAdornment}
                isTravelResultsFilters={isTravelResultsFilters}
                isOpen={isOpen}
                //@ts-ignore
                InputProps={getInputProps({
                  ...getCurrentInputProps(),
                  ...getInputClasses(Boolean(selectedItem.subLabels)),
                  ...setupEventProps({
                    isOpen,
                    closeMenu,
                    selectItem,
                    currentSelectedItem,
                    clearAndOpen: () => {
                      if (!readOnly && !disabled) {
                        clearSelection();
                        openMenu();
                      }
                    },
                  }),
                })}
                onClick={onClick}
                label={label}
                error={error}
                focused={focused}
                hasSelectedValue={!!currentSelectedItem}
                disabled={disabled || false}
                readOnly={readOnly || false}
                spaceForAdornmentReserved={getHasSublabels()}
                name={name}
                margin={margin}
                inputProps={{
                  ...inputProps,
                  onBlur: () => {
                    closeMenu();
                    selectItem(currentSelectedItem);
                  },
                }}
              />
              {isOpen ? (
                <SelectWithFilterPaper
                  inputValue={inputValue}
                  getItemProps={getItemProps}
                  dictionary={dictionary}
                  answerGroups={answerGroups}
                  classes={{ root: classes.paper }}
                  selected={highlightedIndex}
                  cols={cols ? cols : 1}
                  width={width}
                />
              ) : null}
              {selectedItem.subLabels && (
                <div className={classes.selectedItemSubtitle}>
                  {selectedItem.subLabels.map((subLabel: string) => (
                    <span key={subLabel}>{subLabel}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </Downshift>
      </div>
    </Suspense>
  );
};

export default SelectWithFilter;
